import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden brand-bg">
        <img
          src="https://images.unsplash.com/photo-1510758588288-aa8cf9445f5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="campus"
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="relative z-10 flex items-center gap-3">
          <ShieldCheck className="h-8 w-8" />
          <span className="font-heading font-bold text-xl">ARU Supervision Platform</span>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            Human-led,<br />AI-supported<br />research supervision
          </h1>
          <p className="text-white/85 max-w-md">
            Governed AI assistants prepare rubric-based diagnostics; qualified human supervisors
            make every binding decision. Multi-tenant, auditable, and built for scale.
          </p>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Sparkles className="h-4 w-4" /> AI prepares · Humans decide
          </div>
        </div>
        <p className="relative z-10 text-white/70 text-sm">Grow | Prosper | Excel</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-foreground">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your institution workspace</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@institution.edu" required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-muted-foreground">
            Access is provisioned by your institution. Contact your Research Office or
            administrator if you need an account or have trouble signing in.
          </p>
        </div>
      </div>
    </div>
  );
}
